import { ExternalLink, Factory, Globe, MapPin, Package2, Share2, Users } from 'lucide-react';
import { PropsWithChildren, useMemo, useState } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@leadsnavi/ui-kit';
import InsIcon from '@/assets/icons/ins.svg?react';
import LinkedInIcon from '@/assets/icons/linkedin.svg?react';
import TwitterIcon from '@/assets/icons/x.svg?react';
import YoutubeIcon from '@/assets/icons/youtube.svg?react';
import FacebookIcon from '@/assets/icons/facebook.svg?react';
import { IApiEaseSourcingCompanyDetailResultVO } from '@/services/swagger/globalSearch/models';
import { TextWithSeeMore } from '../../../base/text-truncate';
import { GoogleStreetViewDialog } from '@/common/base/google-street-view-dialog';
import { Ellipse } from '@/common/base/ellipse';
import { TextTranslate } from '@/components/translate-icon';
// import streetImageUrl from './imgs/street-image.png';

interface CompanyInfoProps {
  info: IApiEaseSourcingCompanyDetailResultVO;
  socialMedia?: Array<{ name: string; url: string; icon: string }>;
  images?: Array<{
    id: string;
    src: string;
    alt: string;
    isStreetView: boolean;
    isMore?: boolean;
    count?: number;
  }>;
}

interface FieldViewProps {
  label: string;
  icon?: React.ReactNode;
}

const buildSocialMedia = (info: IApiEaseSourcingCompanyDetailResultVO) => {
  return [
    { name: 'Facebook', url: info.facebook, icon: <FacebookIcon /> },
    { name: 'Instagram', url: info.instagram, icon: <InsIcon /> },
    { name: 'LinkedIn', url: info.linkedin, icon: <LinkedInIcon /> },
    { name: 'Twitter', url: info.twitter, icon: <TwitterIcon /> },
    { name: 'YouTube', url: info.youtube, icon: <YoutubeIcon /> },
  ].filter(item => item.url);
};

const FieldView = ({ label, icon, children }: PropsWithChildren<FieldViewProps>) => (
  <div className="">
    <div className=" flex gap-1 items-center mb-1">
      {icon}
      <p className="text-sm  text-[#525252]">{label}:</p>
    </div>
    <div className="text-[#262626] text-sm">{children}</div>
  </div>
);

// const maxProductCount = 3;
export const CompanyInfoComponent: React.FC<CompanyInfoProps> = props => {
  const { info } = props;
  const socialMediaList = useMemo(() => buildSocialMedia(info), [info]);
  const [open, setOpen] = useState(false);
  const location = useMemo(() => {
    if (!info.lat || !info.lng) return '';
    return `${info.lat},${info.lng}`;
  }, [info.lat, info.lng]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="text-[#171717] font-semibold text-xl mb-4">Company Info</div>
      <div className="mb-6 text-[#525252]">
        <TextTranslate
          resident={true}
          inline
          text={info.overviewDescription}
          renderText={(text, icon) => {
            return <TextWithSeeMore text={text} lineLimit={3} translateIcon={icon} />;
          }}
        />
        {/* 最多展示三行
         */}
        {/* <TextWithSeeMore text={info.overviewDescription} lineLimit={3} /> */}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {info.staff ? (
          <FieldView label="Number of Employees" icon={<Users color="#525252" size={16} />}>
            <Ellipse text={info.staff} />
          </FieldView>
        ) : null}

        {info.domain ? (
          <FieldView label="Website" icon={<Globe color="#525252" size={16} />}>
            <a
              href={info.domain}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#2563EB] hover:underline  underline"
            >
              {info.domain}
            </a>
          </FieldView>
        ) : null}
        {info.industrySet.length > 0 ? (
          <FieldView label="Industry" icon={<Factory color="#525252" size={16} />}>
            <Ellipse text={info.industrySet.join(', ')} />
          </FieldView>
        ) : null}

        {info.recProductList && info.recProductList.length > 0 ? (
          <FieldView label="Main products" icon={<Package2 color="#525252" size={16} />}>
            <Ellipse text={info.recProductList.join(', ')} />
          </FieldView>
        ) : null}
        {socialMediaList.length > 0 ? (
          <FieldView label="Social media" icon={<Share2 color="#525252" size={16} />}>
            <div className="flex gap-2 items-center mt-1">
              {socialMediaList.map(item => {
                return (
                  <a href={item.url} target="_blank" rel="noopener noreferrer" key={item.name}>
                    {item.icon}
                  </a>
                );
              })}
            </div>
          </FieldView>
        ) : null}
        {info.location ? (
          <FieldView label="Address" icon={<MapPin color="#525252" size={16} />}>
            <div className="flex items-center justify-start gap-2">
              <p className="text-sm leading-6">
                <span className="leading-full">{info.location}</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a
                      href={`https://www.google.com/maps?q=${info.location}&mrt=loc`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1 underline align-text-bottom ml-2 leading-1
"
                    >
                      <ExternalLink size={16} />
                      Google Maps
                    </a>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[315px]">
                    <p>{info.recProductList}</p>
                  </TooltipContent>
                </Tooltip>
              </p>
            </div>
          </FieldView>
        ) : null}
      </div>

      {/* <div className="flex gap-2 items-center mt-5">
        {location ? (
          <div
            className="w-[137px] h-[97px] shrink-0  relative overflow-hidden rounded-[4px] cursor-pointer"
            onClick={() => {
              setOpen(true);
            }}
          >
            <img src={streetImageUrl} alt="" />
          </div>
        ) : null}
        {info.mapPics.map((link, index) => {
          if (index >= maxProductCount) return null;
          // 包含当前还有多少个图片
          const count = info.mapPics.length - index;
          return (
            <div
              key={index}
              className="w-[173px] h-[97px] shrink-0  relative overflow-hidden rounded-[4px] cursor-pointer"
              onClick={() => {
                openImagePreview({
                  images: info.mapPics,
                  initialIndex: index,
                  onClose: () => {
                    console.log('图片预览已关闭');
                  },
                });
              }}
            >
              <img
                className="w-full 
                absolute
                top-1/2
                left-1/2
                transform
                translate-x-[-50%]
                translate-y-[-50%]
                "
                src={link}
                alt={link || ''}
              />
              {count > 1 && index + 1 === maxProductCount ? (
                <div className=" absolute top-0 right-0 size-full z-10  text-white text-xs px-1  [background:rgba(0,0,0,0.60)] flex items-center justify-center  cursor-pointer">
                  +{count - 1}
                  <ChevronRight size={16} />
                </div>
              ) : null}
            </div>
          );
        })}
      </div> */}
      <GoogleStreetViewDialog open={open} onOpenChange={() => setOpen(false)} location={location} />
    </div>
  );
};
